import { useState } from "react";
import { Camera, Brain, FolderOpen, Calculator, Bell, Users, CheckCircle2, ShieldCheck, Lock, Sliders, Sparkles, Info, CalendarCheck, ArrowRight, Menu, Phone, Wrench } from "lucide-react";

// ─────────────────────────────────────────────────────────────────
//  Pricing — Public marketing page (Desktop English only in this pass)
//  Asset-first hybrid workflow. Only one image asset used: the
//  approved Levi PNG at /brand/how-it-works/levi_headshot_approved.png.
//  All other UI is native React/CSS.
// ─────────────────────────────────────────────────────────────────

const COPY_PRICING = {
  en: {
    kicker: "FINALLY, AFFORDABLE SOFTWARE FOR CONTRACTORS.",
    headline_a: "One simple price.",
    headline_b: "The full JobClose AI +",
    headline_c: "Levi experience.",
    lede: "Everything you need to win more jobs, run them smoothly, and close with confidence.",
    toggle: { monthly: "Monthly", annual: "Annual", saveBadge: "Save $349" },
    monthly: {
      price: "$129",
      per: "/month",
      included: "2 full users included",
      addon_a: "Additional users:",
      addon_b: "$20/month each",
      bullets: [
        "No mandatory setup fee",
        "Standard onboarding included",
        "Cancel anytime",
      ],
      cta: "Start Your 7-Day Free Trial",
    },
    annual: {
      badge: "BEST VALUE",
      price: "$1,199",
      per: "/year",
      included: "2 full users included",
      addon_a: "Additional users:",
      addon_b: "$20/month each  ·  $240/year per additional user",
      save_a: "Save $349 per year",
      save_b: "Equivalent to about $100/month",
      bullets: [
        "No mandatory setup fee",
        "Standard onboarding included",
        "Cancel anytime",
      ],
      cta: "Start Your 7-Day Free Trial",
    },
    full_experience_a: "The full JobClose AI + Levi experience.",
    full_experience_b: "No stripped-down plans. No feature maze.",
    value_strip: [
      { Icon: ShieldCheck, title: "Your pricing.", body_a: "No hidden fees.", body_b: "No surprises." },
      { Icon: Lock, title: "Your data.", body_a: "You own your data.", body_b: "You decide who sees it." },
      { Icon: Sliders, title: "Your control.", body_a: "Your business.", body_b: "Your decisions." },
      { Icon: Sparkles, title: "Levi included.", body_a: "AI that works for you —", body_b: "not the other way around." },
    ],
    caps: [
      { Icon: Camera, label: "Captures every detail" },
      { Icon: Brain, label: "Understands the job" },
      { Icon: FolderOpen, label: "Organizes everything" },
      { Icon: Calculator, label: "Applies your pricing" },
      { Icon: Bell, label: "Flags gaps & follows up" },
      { Icon: Users, label: "Keeps everyone aligned" },
    ],
    website: {
      kicker: "OPTIONAL ADD-ON",
      title: "Need a professional website too?",
      lede: "Contractor websites starting at",
      price: "$399",
      per: "/year",
      includes: "Includes:",
      bullets: [
        "1 year of hosting",
        "Professional contractor-focused design",
        "Mobile-friendly design",
        "Contact forms and lead capture",
        "Built to help you look established and credible",
      ],
      disclaimer_a: "Domain name purchased separately by the contractor.",
      disclaimer_b: "The contractor owns and controls their domain.",
      cta: "Add a Website",
    },
    trial: {
      kicker: "READY TO SEE IT IN ACTION?",
      title: "Start your 7-day free trial.",
      body_a: "No credit card needed.",
      body_b: "Guided setup included.",
      cta: "Start Your 7-Day Free Trial",
    },
  },
};
COPY_PRICING.es = {
  kicker: "SOFTWARE FINALMENTE ASEQUIBLE PARA CONTRATISTAS.",
  headline_a: "Un precio simple.",
  headline_b: "La experiencia completa de",
  headline_c: "JobClose AI + Levi.",
  lede: "Todo lo que necesitas para ganar más trabajos, ejecutarlos sin problemas y cerrarlos con confianza.",
  toggle: { monthly: "Mensual", annual: "Anual", saveBadge: "Ahorra $349" },
  monthly: {
    price: "$129",
    per: "/mes",
    included: "2 usuarios completos incluidos",
    addon_a: "Usuarios adicionales:",
    addon_b: "$20/mes cada uno",
    bullets: [
      "Sin tarifa de configuración obligatoria",
      "Configuración inicial estándar incluida",
      "Cancela cuando quieras",
    ],
    cta: "Comienza tu prueba gratis de 7 días",
  },
  annual: {
    badge: "MEJOR VALOR",
    price: "$1,199",
    per: "/año",
    included: "2 usuarios completos incluidos",
    addon_a: "Usuarios adicionales:",
    addon_b: "$20/mes cada uno  ·  $240/año por usuario adicional",
    save_a: "Ahorra $349 al año",
    save_b: "Equivalente a unos $100/mes",
    bullets: [
      "Sin tarifa de configuración obligatoria",
      "Configuración inicial estándar incluida",
      "Cancela cuando quieras",
    ],
    cta: "Comienza tu prueba gratis de 7 días",
  },
  full_experience_a: "La experiencia completa de JobClose AI + Levi.",
  full_experience_b: "Sin planes recortados. Sin laberinto de funciones.",
  value_strip: [
    { Icon: ShieldCheck, title: "Tus precios.", body_a: "Sin tarifas ocultas.", body_b: "Sin sorpresas." },
    { Icon: Lock, title: "Tus datos.", body_a: "Tú eres dueño de tus datos.", body_b: "Tú decides quién los ve." },
    { Icon: Sliders, title: "Tu control.", body_a: "Tu negocio.", body_b: "Tus decisiones." },
    { Icon: Sparkles, title: "Levi incluido.", body_a: "IA que trabaja para ti —", body_b: "no al revés." },
  ],
  caps: [
    { Icon: Camera, label: "Captura cada detalle" },
    { Icon: Brain, label: "Entiende el trabajo" },
    { Icon: FolderOpen, label: "Organiza todo" },
    { Icon: Calculator, label: "Aplica tus precios" },
    { Icon: Bell, label: "Detecta huecos y da seguimiento" },
    { Icon: Users, label: "Mantiene a todos alineados" },
  ],
  website: {
    kicker: "COMPLEMENTO OPCIONAL",
    title: "¿Necesitas también un sitio web profesional?",
    lede: "Sitios web para contratistas desde",
    price: "$399",
    per: "/año",
    includes: "Incluye:",
    bullets: [
      "1 año de hosting",
      "Diseño profesional enfocado en contratistas",
      "Diseño adaptable a móviles",
      "Formularios de contacto y captura de prospectos",
      "Diseñado para que te veas establecido y creíble",
    ],
    disclaimer_a: "El contratista compra el nombre de dominio por separado.",
    disclaimer_b: "El contratista es dueño y controla su dominio.",
    cta: "Agregar un Sitio Web",
  },
  trial: {
    kicker: "¿LISTO PARA VERLO EN ACCIÓN?",
    title: "Comienza tu prueba gratis de 7 días.",
    body_a: "No se necesita tarjeta de crédito.",
    body_b: "Configuración guiada incluida.",
    cta: "Comienza tu prueba gratis de 7 días",
  },
};

const PricingBody = ({ lang = "en" }) => {
  const c = COPY_PRICING[lang] || COPY_PRICING.en;
  const [billing, setBilling] = useState("annual");
  return (
    <main
      className="relative overflow-hidden bg-gradient-to-b from-[#F5F9FF] via-white to-[#F5F9FF]"
      data-testid="pricing-body"
    >
      {/* Restrained soft-blue environmental treatment */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 45% at 50% 8%, rgba(191,219,254,0.55) 0%, rgba(147,197,253,0.22) 40%, rgba(255,255,255,0) 78%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-8 lg:pt-10 pb-24">
        {/* Hero row: copy + Levi with flanking capability chips */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-10 items-center" data-testid="pricing-hero">
          <div>
            <div className="inline-block px-3 py-1 rounded-full bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] text-[11px] font-bold tracking-[0.14em]">
              {c.kicker}
            </div>
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-[var(--jc-text)] text-[34px] lg:text-[52px] leading-[1.02]">
              {c.headline_a}
            </h1>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[var(--jc-text)] text-[26px] lg:text-[38px] leading-[1.08]">
              {c.headline_b}<br />
              <span className="text-[var(--jc-blue)]">{c.headline_c}</span>
            </h2>
            <p className="mt-5 text-[16px] lg:text-[17px] leading-[1.55] text-[var(--jc-text-soft)] font-medium max-w-[520px]">
              {c.lede}
            </p>
          </div>
          <LeviOrbit caps={c.caps} />
        </section>

        {/* Billing toggle */}
        <div className="mt-8 flex justify-center" data-testid="pricing-toggle-wrap">
          <div className="inline-flex items-center rounded-full bg-white ring-1 ring-black/8 shadow-sm p-1" role="group" aria-label="Billing frequency">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              aria-pressed={billing === "monthly"}
              data-testid="pricing-toggle-monthly"
              className={"px-5 py-2 text-[14px] font-semibold rounded-full transition " + (billing === "monthly" ? "bg-[var(--jc-blue)] text-white shadow" : "text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]")}
            >
              {c.toggle.monthly}
            </button>
            <button
              type="button"
              onClick={() => setBilling("annual")}
              aria-pressed={billing === "annual"}
              data-testid="pricing-toggle-annual"
              className={"px-5 py-2 text-[14px] font-semibold rounded-full transition inline-flex items-center gap-2 " + (billing === "annual" ? "bg-[var(--jc-blue)] text-white shadow" : "text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]")}
            >
              {c.toggle.annual}
              <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10.5px] font-bold tracking-wide">
                {c.toggle.saveBadge}
              </span>
            </button>
          </div>
        </div>

        {/* Two price cards */}
        <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pricing-cards">
          <PriceCard tier={c.monthly} highlighted={billing === "monthly"} />
          <PriceCard tier={c.annual} highlighted={billing === "annual"} annual />
        </section>

        {/* Full-experience callout */}
        <section
          className="mt-10 rounded-2xl bg-gradient-to-r from-[var(--jc-blue)] to-[#1E40AF] text-white p-6 lg:p-7 flex items-center gap-4 shadow-[0_18px_40px_-16px_rgba(37,99,235,0.55)]"
          data-testid="pricing-full-experience"
        >
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/15 ring-1 ring-white/25 flex-shrink-0">
            <ShieldCheck size={22} strokeWidth={2.4} />
          </span>
          <div>
            <div className="font-display font-extrabold text-[20px] leading-tight">{c.full_experience_a}</div>
            <div className="mt-1 text-[14.5px] font-medium text-white/90">{c.full_experience_b}</div>
          </div>
        </section>

        {/* Value strip */}
        <section
          className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="pricing-value-strip"
        >
          {c.value_strip.map((v, i) => {
            const Icon = v.Icon;
            return (
              <div key={i} className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5" data-testid={`pricing-value-${i}`}>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[var(--jc-blue-soft)] text-[var(--jc-blue)]">
                  <Icon size={22} strokeWidth={2.2} />
                </span>
                <div className="mt-3.5 font-display font-extrabold text-[17px] text-[var(--jc-text)]">{v.title}</div>
                <div className="mt-1.5 text-[13.5px] leading-[1.45] text-[var(--jc-text-soft)] font-medium">
                  <div>{v.body_a}</div>
                  <div>{v.body_b}</div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Website add-on */}
        <section
          className="mt-8 rounded-3xl bg-white ring-1 ring-black/5 shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]"
          data-testid="pricing-website"
        >
          {/* Device mockup — simplest deterministic implementation */}
          <div className="relative bg-gradient-to-br from-[#EEF4FF] to-[#DCE7FB] p-6 lg:p-10 flex items-center justify-center min-h-[260px] lg:min-h-[360px]">
            <DeviceMockup />
          </div>
          {/* Copy */}
          <div className="p-6 lg:p-10 flex flex-col">
            <div className="inline-block self-start px-2.5 py-1 rounded-full bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] text-[10.5px] font-bold tracking-[0.14em]">
              {c.website.kicker}
            </div>
            <h3 className="mt-4 font-display font-extrabold text-[22px] lg:text-[28px] text-[var(--jc-text)] leading-tight">
              {c.website.title}
            </h3>
            <p className="mt-4 text-[14.5px] text-[var(--jc-text-soft)] font-medium">
              {c.website.lede}
            </p>
            <div className="mt-1.5 flex items-baseline gap-1.5">
              <span className="font-display font-extrabold text-[44px] text-[var(--jc-blue)] tracking-tight leading-none">{c.website.price}</span>
              <span className="text-[15px] text-[var(--jc-text-soft)] font-semibold">{c.website.per}</span>
            </div>
            <div className="mt-5 text-[13px] font-bold text-[var(--jc-text)] uppercase tracking-wider">{c.website.includes}</div>
            <ul className="mt-2.5 space-y-2">
              {c.website.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-[14.5px] text-[var(--jc-text)]">
                  <CheckCircle2 size={17} className="text-[var(--jc-blue)] flex-shrink-0 mt-[2px]" strokeWidth={2.4} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-[var(--jc-blue-soft)]/70 ring-1 ring-[var(--jc-blue)]/25 px-3.5 py-3">
              <Info size={17} className="text-[var(--jc-blue)] flex-shrink-0 mt-[2px]" strokeWidth={2.4} />
              <div className="text-[12.5px] leading-[1.45] text-[var(--jc-text)] font-semibold">
                <div>{c.website.disclaimer_a}</div>
                <div className="text-[var(--jc-text-soft)] font-medium">{c.website.disclaimer_b}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Final trial CTA */}
        <section
          className="mt-14 rounded-3xl bg-gradient-to-br from-[#F8FBFF] to-[#EAF2FF] ring-1 ring-[var(--jc-blue)]/12 p-6 lg:p-12 text-center shadow-sm"
          data-testid="pricing-trial-cta"
        >
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--jc-blue)] text-white shadow-[0_10px_30px_-8px_rgba(59,130,246,0.9)] ring-4 ring-[var(--jc-blue)]/15">
            <CalendarCheck size={26} strokeWidth={2.4} />
          </span>
          <div className="mt-4 text-[11px] font-bold tracking-[0.14em] text-[var(--jc-blue)]">{c.trial.kicker}</div>
          <h3 className="mt-2 font-display font-extrabold text-[26px] lg:text-[38px] text-[var(--jc-text)] tracking-tight leading-tight">
            {c.trial.title}
          </h3>
          <div className="mt-3 text-[15.5px] font-medium text-[var(--jc-text-soft)]">
            <div>{c.trial.body_a}</div>
            <div>{c.trial.body_b}</div>
          </div>
          <button
            type="button"
            data-testid="pricing-trial-cta-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[var(--jc-blue)] hover:brightness-110 transition text-white font-bold px-7 py-3.5 text-[15px] shadow-[0_18px_40px_-16px_rgba(37,99,235,0.6)]"
          >
            {c.trial.cta}<ArrowRight size={16} strokeWidth={2.6} />
          </button>
        </section>
      </div>
    </main>
  );
};

// ─────── Levi flanked by 3-left / 3-right capability pairs ───────
// Structural symmetry via a shared 3-column grid: equal 1fr cap
// tracks + auto Levi track. Levi sits on the true centerline of
// the composition. Each cap li uses justify-end/start so icons pin
// to the Levi-facing inner edge; rows share min-h-[44px] so left
// and right rows align on identical baselines.
const LeviOrbit = ({ caps }) => {
  const leftCaps = caps.slice(0, 3);
  const rightCaps = caps.slice(3, 6);
  return (
    <div className="relative flex items-center justify-center" data-testid="pricing-levi-orbit">
      {/* Ambient blue bloom behind Levi (never on the PNG) */}
      <div
        aria-hidden="true"
        className="absolute inset-6 rounded-full blur-3xl opacity-70"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 55%, rgba(96,165,250,0.42) 0%, rgba(37,99,235,0.18) 45%, rgba(255,255,255,0) 78%)",
        }}
      />

      {/* MOBILE: vertical stack — Levi anchor image, then 6 caps as 2-col grid below.
          Desktop composition (lg+) is preserved untouched. */}
      <div className="relative w-full flex flex-col items-center gap-6 lg:hidden" data-testid="pricing-levi-orbit-mobile">
        <img
          src="/brand/how-it-works/levi_headshot_approved.png"
          alt="Levi"
          className="relative z-0 w-[260px] max-w-full h-auto select-none"
          draggable={false}
          loading="eager"
          decoding="sync"
          data-testid="pricing-levi-img-mobile"
        />
        <ul className="grid grid-cols-2 gap-x-3 gap-y-3 w-full" data-testid="pricing-caps-mobile">
          {caps.map((cap, i) => (
            <PricingCap key={i} cap={cap} side="right" testid={`pricing-cap-m-${i}`} />
          ))}
        </ul>
      </div>

      {/* DESKTOP: locked flanked composition */}
      <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 hidden lg:grid">
        {/* LEFT caps · right-anchored toward Levi · TEXT → ICON */}
        <ul className="space-y-3 relative z-10 justify-self-end" style={{ marginRight: "-100px" }} data-testid="pricing-caps-left">
          {leftCaps.map((cap, i) => (
            <PricingCap key={i} cap={cap} side="left" testid={`pricing-cap-${i}`} />
          ))}
        </ul>

        {/* Levi center */}
        <img
          src="/brand/how-it-works/levi_headshot_approved.png"
          alt="Levi"
          className="relative z-0 min-w-[505px] w-[505px] h-auto select-none"
          draggable={false}
          loading="eager"
          decoding="sync"
          data-testid="pricing-levi-img"
        />

        {/* RIGHT caps · left-anchored toward Levi · ICON → TEXT */}
        <ul className="space-y-3 relative z-10 justify-self-start" style={{ marginLeft: "-100px" }} data-testid="pricing-caps-right">
          {rightCaps.map((cap, i) => (
            <PricingCap key={i} cap={cap} side="right" testid={`pricing-cap-${i + 3}`} />
          ))}
        </ul>
      </div>
    </div>
  );
};

// Shared capability pair · one flex unit with 8 px internal gap.
const PricingCap = ({ cap, side, testid }) => {
  const Icon = cap.Icon;
  const isLeft = side === "left";
  const iconTile = (
    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white ring-1 ring-[var(--jc-blue)]/15 text-[var(--jc-blue)] shadow-[0_10px_24px_-10px_rgba(37,99,235,0.55)] flex-shrink-0">
      <Icon size={18} strokeWidth={2.2} />
    </span>
  );
  const label = (
    <span className={`text-[13px] font-semibold text-[var(--jc-text)] leading-tight lg:whitespace-nowrap ${isLeft ? "text-right" : "text-left"}`}>
      {cap.label}
    </span>
  );
  return (
    <li
      className={`flex items-center min-h-[44px] ${isLeft ? "justify-end" : "justify-start"}`}
      style={{ gap: "8px" }}
      data-testid={testid}
    >
      {isLeft ? (<>{label}{iconTile}</>) : (<>{iconTile}{label}</>)}
    </li>
  );
};

import { isNativeShell } from "@/lib/platform";

// ─────── Price card ·──────
// Shared structural equal-height layout: flex-column with the CTA
// anchored at the bottom via mt-auto so both cards render their
// "Start Your 7-Day Free Trial" button on identical baselines
// regardless of the annual card's extra "Save $349" row.
//
// Roadmap #2 · Strategy A · the trial-start button is a
// subscription-precursor CTA (per current Apple 3.1.3(f) reviewer
// practice) even though its destination is the free /signup form.
// The button — and only the button — is gated on native.  The
// price card body (price, benefits, factual bullets) renders on
// both web and native so contractors continue to see canonical
// pricing.  Web behaviour unchanged.
const PriceCard = ({ tier, highlighted, annual }) => (
  <div
    className={
      "relative rounded-3xl bg-white p-7 lg:p-8 shadow-sm transition ring-1 flex flex-col h-full " +
      (highlighted ? "ring-[var(--jc-blue)]/40 shadow-[0_24px_50px_-24px_rgba(37,99,235,0.35)]" : "ring-black/8")
    }
    data-testid={annual ? "pricing-card-annual" : "pricing-card-monthly"}
  >
    {annual && tier.badge && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-emerald-500 text-white px-3 py-1 text-[10.5px] font-bold tracking-wide shadow-sm">
        {tier.badge}
      </div>
    )}
    <div className="flex items-baseline gap-1.5">
      <span className="font-display font-extrabold text-[54px] text-[var(--jc-text)] tracking-tight leading-none">{tier.price}</span>
      <span className="text-[16px] font-semibold text-[var(--jc-text-soft)]">{tier.per}</span>
    </div>
    <div className="mt-3 text-[14px] font-semibold text-[var(--jc-text)]">{tier.included}</div>
    <div className="mt-4 rounded-xl bg-[var(--jc-blue-soft)]/60 ring-1 ring-[var(--jc-blue)]/15 px-3.5 py-2.5">
      <div className="text-[12.5px] font-semibold text-[var(--jc-text-soft)]">{tier.addon_a}</div>
      <div className="text-[13.5px] font-bold text-[var(--jc-text)]">{tier.addon_b}</div>
    </div>
    {annual && (
      <div className="mt-4 flex items-start gap-2 text-[13px] leading-[1.4]">
        <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-[2px]" strokeWidth={2.6} />
        <div>
          <div className="font-bold text-[var(--jc-text)]">{tier.save_a}</div>
          <div className="text-[var(--jc-text-soft)] font-medium">{tier.save_b}</div>
        </div>
      </div>
    )}
    <ul className="mt-5 space-y-2.5">
      {tier.bullets.map((b, i) => (
        <li key={i} className="flex items-center gap-2 text-[14px] text-[var(--jc-text)]">
          <CheckCircle2 size={16} className="text-[var(--jc-blue)] flex-shrink-0" strokeWidth={2.4} />
          <span>{b}</span>
        </li>
      ))}
    </ul>
    <button
      type="button"
      data-testid={annual ? "pricing-card-annual-cta" : "pricing-card-monthly-cta"}
      className={
        "mt-auto pt-6 w-full inline-flex items-center justify-center gap-2 rounded-lg font-bold px-5 py-3 text-[14.5px] transition bg-[var(--jc-blue)] text-white shadow-[0_18px_40px_-16px_rgba(37,99,235,0.6)] hover:brightness-110 " +
        (isNativeShell() ? "hidden" : "")
      }
      style={{ /* mt-auto pushes to bottom; pt-6 is spacing, background overridden below */ }}
    >
      {tier.cta}<ArrowRight size={15} strokeWidth={2.6} />
    </button>
  </div>
);

// ─────── Device mockup (simple, deterministic) ───────
const DeviceMockup = () => (
  <div className="relative w-full max-w-[440px] aspect-[5/4]" data-testid="pricing-website-mockup">
    {/* Laptop */}
    <div className="absolute inset-x-0 top-0 rounded-t-xl bg-[#1F2937] p-2 shadow-xl">
      <div className="rounded-md bg-white overflow-hidden aspect-[16/9]">
        {/* Browser chrome */}
        <div className="h-6 bg-[#F3F4F6] flex items-center gap-1.5 px-2 border-b border-black/5">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-yellow-400" />
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="ml-2 flex-1 h-3 rounded-sm bg-white ring-1 ring-black/5" />
        </div>
        {/* Website content */}
        <MiniSite />
      </div>
    </div>
    {/* Laptop base */}
    <div className="absolute inset-x-[-6%] top-[calc(100%*9/16*1.06+6px)] h-2.5 bg-[#111827] rounded-b-md shadow-md" style={{ top: "calc((100% * 0.6) )" }} />
    {/* Phone (overlapping bottom right) */}
    <div className="absolute -right-2 bottom-0 w-[112px] h-[210px] rounded-[22px] bg-[#111827] p-1.5 shadow-2xl ring-1 ring-black/20">
      <div className="w-full h-full rounded-[18px] bg-white overflow-hidden relative">
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-10 h-1.5 rounded-full bg-[#111827]" />
        <div className="pt-4">
          <PhoneMiniSite />
        </div>
      </div>
    </div>
  </div>
);

const MiniSite = () => (
  <div className="h-full flex flex-col">
    {/* Top bar */}
    <div className="px-3 py-2 flex items-center justify-between bg-white border-b border-black/5">
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-[var(--jc-blue)] text-white"><Wrench size={9} strokeWidth={3} /></span>
        <span className="text-[7.5px] font-extrabold text-[var(--jc-text)] tracking-tight">RIVERBEND CONSTRUCTION</span>
      </div>
      <div className="flex items-center gap-1.5 text-[6px] font-bold text-[var(--jc-text-soft)]">
        <span>HOME</span><span>SERVICES</span><span>PROJECTS</span><span>CONTACT</span>
      </div>
    </div>
    {/* Hero */}
    <div className="flex-1 relative bg-gradient-to-br from-[#0B2447] to-[#1E3A8A] text-white px-3 py-3 flex flex-col justify-center">
      <div className="text-[10px] font-display font-extrabold leading-tight">Quality work.<br />Built on trust.</div>
      <div className="mt-1 text-[6px] font-medium text-white/80">Serving contractors and homeowners for 20+ years.</div>
      <div className="mt-1.5 inline-flex items-center gap-1 self-start rounded bg-[var(--jc-blue)] px-1.5 py-0.5 text-[6px] font-bold">Request a Quote <ArrowRight size={7} strokeWidth={3} /></div>
    </div>
  </div>
);

const PhoneMiniSite = () => (
  <div className="px-1.5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1">
        <span className="inline-flex items-center justify-center w-3 h-3 rounded-sm bg-[var(--jc-blue)] text-white"><Wrench size={7} strokeWidth={3} /></span>
        <span className="text-[5.5px] font-extrabold text-[var(--jc-text)]">RIVERBEND</span>
      </div>
      <Menu size={9} className="text-[var(--jc-text)]" />
    </div>
    <div className="mt-2 rounded-md bg-gradient-to-br from-[#0B2447] to-[#1E3A8A] text-white p-2">
      <div className="text-[8px] font-display font-extrabold leading-tight">Quality work.<br />Built on trust.</div>
      <div className="mt-1 text-[5px] font-medium text-white/80">Serving your area for 20+ years.</div>
    </div>
    <div className="mt-1.5 rounded-sm bg-[var(--jc-blue)] text-white px-1.5 py-1 text-center text-[6px] font-bold">Request a Quote</div>
    <div className="mt-2 text-[6px] font-bold text-[var(--jc-text-soft)] uppercase tracking-wider">Our Services</div>
    <ul className="mt-1 space-y-1">
      {["Painting", "Roofing", "Remodels"].map((s, i) => (
        <li key={i} className="flex items-center gap-1 text-[6.5px] text-[var(--jc-text)]">
          <CheckCircle2 size={7} className="text-[var(--jc-blue)]" strokeWidth={3} />{s}
        </li>
      ))}
    </ul>
    <div className="mt-2 flex items-center gap-1 text-[6px] font-semibold text-[var(--jc-text-soft)]">
      <Phone size={7} strokeWidth={2.6} /> (555) 010-0142
    </div>
  </div>
);

export default PricingBody;
